<script>
	// Stores
	import ClassificationStore from '@/Stores/Racing/ClassificationStore';
	import CompetitionStore from '@/Stores/Sport/CompetitionStore';
	import RaceStore from '@/Stores/Racing/RaceStore';
	import MarketGroupStore from '@/Stores/Sport/MarketGroupStore';
	import MarketStore from '@/Stores/Sport/MarketStore.js';
	import SportcastSelectionStore from '@/Stores/Sport/SportcastSelectionStore.js';
	// Services
	import { notify } from '@/Services/Notify.js';
	// Components
	import Modal from '@/Components/ModalFlex.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	import TextInput from '@/Components/NewTextInput.svelte';
	// Helpers
	import { CurrencyFormat, NumberTransform } from '@/Services/Helpers';

	export let race = undefined;
	export let meeting = undefined;
	export let market = undefined;
	export let marketGroupId = undefined;
	export let event = undefined;
	export let sportcast_selection = undefined;

	export let open = false;

	let sending = false;

	window.scrollTo(0, 0);

	$: eventName = '';
	$: classification = (race || event) && $ClassificationStore && $CompetitionStore && getClassification();

	$: currentSelectionLiabilityLimit = classification && getCurrentSelectionLiability();
	$: newSelectionLiabilityLimit = NumberTransform(currentSelectionLiabilityLimit, false);
	$: currentMultiSelectionLiabilityLimit = classification && getCurrentMultiSelectionLiability();
	$: newMultiSelectionLiabilityLimit = NumberTransform(currentMultiSelectionLiabilityLimit, false);

	function getClassification() {
		if (race !== undefined) {
			return $ClassificationStore.firstWhere('id', race.classification.id.toString());
		}
		if (market !== undefined || sportcast_selection !== undefined) {
			let competition = $CompetitionStore.firstWhere('id', event.competition.id);
			if (competition !== null) {
				return $ClassificationStore.firstWhere('id', competition.classification.id.toString());
			}
		}
	}

	function getCurrentSelectionLiability() {
		if (race !== undefined) {
			return race && race.liabilities && race.liabilities.settings && race.liabilities.settings.win_selection_limit
				? race.liabilities.settings.win_selection_limit
				: classification && classification.selection_liability_limit !== null
					? classification.selection_liability_limit
					: 0;
		}
		if (market !== undefined) {
			return market && market.liabilities && market.liabilities.settings && market.liabilities.settings.selection_limit
				? market.liabilities.settings.selection_limit
				: classification && classification.selection_liability_limit !== null
					? classification.selection_liability_limit
					: 0;
		}
		if (sportcast_selection !== undefined) {
			return sportcast_selection &&
				sportcast_selection.liabilities &&
				sportcast_selection.liabilities.settings &&
				sportcast_selection.liabilities.settings.selection_limit
				? sportcast_selection.liabilities.settings.selection_limit
				: classification && classification.sgm_selection_liability_limit !== null
					? classification.sgm_selection_liability_limit
					: 0;
		}

		return 0;
	}

	function getCurrentMultiSelectionLiability() {
		if (race !== undefined) {
			return race &&
				race.liabilities &&
				race.liabilities.settings &&
				race.liabilities.settings.win_multi_selections_limit
				? race.liabilities.settings.win_multi_selections_limit
				: classification && classification.multi_selections_liability_limit !== null
					? classification.multi_selections_liability_limit
					: 0;
		}
		if (market !== undefined) {
			return market &&
				market.liabilities &&
				market.liabilities.settings &&
				market.liabilities.settings.multi_selections_limit
				? market.liabilities.settings.multi_selections_limit
				: classification && classification.multi_selections_liability_limit !== null
					? classification.multi_selections_liability_limit
					: 0;
		}
		if (sportcast_selection !== undefined) {
			return sportcast_selection &&
				sportcast_selection.liabilities &&
				sportcast_selection.liabilities.settings &&
				sportcast_selection.liabilities.settings.multi_selections_limit
				? sportcast_selection.liabilities.settings.multi_selections_limit
				: classification && classification.sgm_multi_selections_liability_limit !== null
					? classification.sgm_multi_selections_liability_limit
					: 0;
		}

		return 0;
	}

	function getEventName() {
		if (race !== undefined) {
			return meeting.name + ' Race ' + race.race_number + ' - ' + race.name;
		}
		if (market !== undefined) {
			return event.name + ' - ' + market.name;
		}
		if (sportcast_selection !== undefined) {
			return event.name + ' - ' + sportcast_selection.name;
		}

		return '';
	}

	function alterAmount(adjustmentValue) {
		newSelectionLiabilityLimit = newSelectionLiabilityLimit + adjustmentValue;
	}
	function alterMultiAmount(adjustmentValue) {
		newMultiSelectionLiabilityLimit = newMultiSelectionLiabilityLimit + adjustmentValue;
	}

	function updateSelectionLiabilityLimit() {
		sending = true;
		if (race !== undefined) {
			race
				.adjustLiabilityLimit(
					newSelectionLiabilityLimit == 0 ? null : parseInt(newSelectionLiabilityLimit.toString()) * 100,
					newMultiSelectionLiabilityLimit == 0 ? null : parseInt(newMultiSelectionLiabilityLimit.toString()) * 100
				)
				.then(() => {
					notify({
						type: 'success',
						title: eventName,
						message: 'Liability limit successfully updated',
						timeout: 3
					});
					RaceStore.getRaceWithDetails(race.id, meeting.id);
					open = false;
				})
				.catch((e) => {
					notify({
						type: 'danger',
						title: 'Error!',
						message: (e.error && e.error[0]) || 'Failed to alter liability limit',
						timeout: 5
					});
				})
				.finally(() => (sending = false));
		}
		if (market !== undefined) {
			market
				.adjustLiabilityLimit(
					newSelectionLiabilityLimit == 0 ? null : parseInt(newSelectionLiabilityLimit.toString()) * 100,
					newMultiSelectionLiabilityLimit == 0 ? null : parseInt(newMultiSelectionLiabilityLimit.toString()) * 100
				)
				.then(() => {
					notify({
						type: 'success',
						title: eventName,
						message: 'Liability limit successfully updated',
						timeout: 3
					});
					if (marketGroupId) {
						MarketGroupStore.getMarketGroupDetails(marketGroupId, event.id);
					} else {
						MarketStore.getMarketWithSelections(market.id);
					}
					open = false;
				})
				.catch((e) => {
					notify({
						type: 'danger',
						title: 'Error!',
						message: (e.error && e.error[0]) || 'Failed to alter liability limit',
						timeout: 5
					});
				})
				.finally(() => (sending = false));
		}
		if (sportcast_selection !== undefined) {
			sportcast_selection
				.adjustLiabilityLimit(
					newSelectionLiabilityLimit == 0 ? null : parseInt(newSelectionLiabilityLimit.toString()) * 100,
					newMultiSelectionLiabilityLimit == 0 ? null : parseInt(newMultiSelectionLiabilityLimit.toString()) * 100
				)
				.then(() => {
					notify({
						type: 'success',
						title: eventName,
						message: 'Liability limit successfully updated',
						timeout: 3
					});
					SportcastSelectionStore.getSportcastSelection(sportcast_selection.id);
					open = false;
				})
				.catch((e) => {
					notify({
						type: 'danger',
						title: 'Error!',
						message: (e.error && e.error[0]) || 'Failed to alter liability limit',
						timeout: 5
					});
				})
				.finally(() => (sending = false));
		}
	}
</script>

{#if open}
	<Modal on:close={() => (open = false)}>
		<h2 slot="header" class="pt-2 mb-2 text-xl font-bold text-center">Adjust Selection Liability Limit</h2>
		<div class="pt-1 text-center">{getEventName()}</div>
		<hr class="mt-3" />
		<div class="flex flex-col items-center py-2 font-semibold">
			<p>
				Current Selection Liability Limit: {CurrencyFormat(currentSelectionLiabilityLimit)}
			</p>
			<div class="flex justify-center mt-4 mb-2 space-x-4">
				<button class="btn btn-danger" on:click={() => alterAmount(-25000)}>-$25k</button>
				<button class="btn btn-danger" on:click={() => alterAmount(-10000)}>-$10k</button>
				<button class="btn btn-danger" on:click={() => alterAmount(-5000)}>-$5k</button>
				<button class="btn btn-primary" on:click={() => alterAmount(5000)}>+$5k</button>
				<button class="btn btn-primary" on:click={() => alterAmount(10000)}>+$10k</button>
				<button class="btn btn-primary" on:click={() => alterAmount(25000)}>+25k</button>
			</div>
			<div class="flex flex-row mb-2 justify-center items-center">
				<div>New Selection Liability Limit:</div>
				<TextInput
					name={'new_selection_liability_limit'}
					type="number"
					required={true}
					prefix="$"
					min="0"
					className="text-left ml-3"
					placeholder="New selection liability limit:"
					step="0.01"
					on:change={(event) => (newSelectionLiabilityLimit = Math.round(parseFloat(event.target.value)))}
					on:keyup={(event) => {
						if (event.keyCode === 13) {
							newSelectionLiabilityLimit = Math.round(parseFloat(event.target.value));
						}
					}}
					value={newSelectionLiabilityLimit}
				/>
			</div>
		</div>
		<hr class="mb-2" />

		<div class="flex flex-col items-center py-2 font-semibold">
			<p>
				Current Multi Selection Liability Limit: {CurrencyFormat(currentMultiSelectionLiabilityLimit)}
			</p>
			<div class="flex justify-center mt-4 mb-2 space-x-4">
				<button class="btn btn-danger" on:click={() => alterMultiAmount(-25000)}>-$25k</button>
				<button class="btn btn-danger" on:click={() => alterMultiAmount(-10000)}>-$10k</button>
				<button class="btn btn-danger" on:click={() => alterMultiAmount(-5000)}>-$5k</button>
				<button class="btn btn-primary" on:click={() => alterMultiAmount(5000)}>+$5k</button>
				<button class="btn btn-primary" on:click={() => alterMultiAmount(10000)}>+$10k</button>
				<button class="btn btn-primary" on:click={() => alterMultiAmount(25000)}>+25k</button>
			</div>
			<div class="flex flex-row mb-2 justify-center items-center">
				<div>New Multi Selection Liability Limit:</div>
				<TextInput
					name={'new_multi_selection_liability_limit'}
					type="number"
					required={true}
					prefix="$"
					min="0"
					className="text-left ml-3"
					placeholder="New multi selection liability limit:"
					step="0.01"
					on:change={(event) => (newMultiSelectionLiabilityLimit = Math.round(parseFloat(event.target.value)))}
					on:keyup={(event) => {
						if (event.keyCode === 13) {
							newMultiSelectionLiabilityLimit = Math.round(parseFloat(event.target.value));
						}
					}}
					value={newMultiSelectionLiabilityLimit}
				/>
			</div>
		</div>

		<div class="flex flex-col px-4">
			<LoadingButton on:click={updateSelectionLiabilityLimit} loading={sending} className="mb-4 btn btn-primary-action"
				>Update
			</LoadingButton>
			<button class="mb-4 btn btn-muted" on:click|preventDefault={() => (open = false)}> Cancel</button>
		</div>
	</Modal>
{/if}
